import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Lightbulb, Loader2, Mail, MinusCircle, Search, ShieldCheck, Undo2, X, XCircle } from 'lucide-react';
import { humanize, toolLabel } from '../agentContextMap';
import { formatCell } from '../resultTable';
import FileCard from './FileCard';

export const ACTION_STATUS_LABEL = {
  PENDING_APPROVAL: 'Awaiting approval',
  EXECUTING: 'Running',
  SUCCEEDED: 'Done',
  FAILED: 'Failed',
  REJECTED: 'Not approved',
  UNDOING: 'Undoing',
  UNDONE: 'Undone',
};

export function StatusIcon({ status }) {
  if (status === 'EXECUTING' || status === 'UNDOING') return <Loader2 size={15} className="dagent-spin" aria-hidden="true" />;
  if (status === 'SUCCEEDED') return <CheckCircle2 size={15} className="dagent-ok" aria-hidden="true" />;
  if (status === 'FAILED') return <XCircle size={15} className="dagent-bad" aria-hidden="true" />;
  if (status === 'UNDONE') return <Undo2 size={15} className="dagent-undone" aria-hidden="true" />;
  return <MinusCircle size={15} className="dagent-muted-icon" aria-hidden="true" />;
}

function Preview({ preview }) {
  if (!preview || typeof preview !== 'object') return null;
  const { body, subject, to, cc, caption, attachments, instructions, replaces, ...rest } = preview;
  const pairs = Object.entries(rest).filter(([, value]) => value !== undefined && value !== null && typeof value !== 'object');
  return (
    <div className="dagent-preview">
      {pairs.length > 0 && (
        <div className="dagent-preview__pairs">
          {pairs.map(([key, value]) => (
            <span key={key}>
              <em>{humanize(key)}</em> {formatCell(value, key)}
            </span>
          ))}
        </div>
      )}
      {(to || cc || subject) && (
        <div className="dagent-preview__pairs">
          {to && (
            <span>
              <em>To</em> {to}
            </span>
          )}
          {cc && (
            <span>
              <em>Cc</em> {cc}
            </span>
          )}
          {subject && (
            <span>
              <em>Subject</em> {subject}
            </span>
          )}
        </div>
      )}
      {body && <blockquote className="dagent-preview__body">{body}</blockquote>}
      {caption && <blockquote className="dagent-preview__body">{caption}</blockquote>}
      {instructions && <blockquote className="dagent-preview__body">{instructions}</blockquote>}
      {Array.isArray(attachments) && attachments.length > 0 && (
        <div className="dagent-preview__files">
          {attachments.map((attachment) => (
            <FileCard
              key={attachment.fileId}
              compact
              file={{ fileId: attachment.fileId, filename: attachment.filename, size: attachment.size, rows: attachment.rows, containsPersonalData: attachment.personalData }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function Diagnosis({ diagnosis }) {
  if (!diagnosis?.explanation) return null;
  return (
    <div className="dagent-diagnosis">
      <div>
        <Search size={12} aria-hidden="true" /> <strong>Why:</strong> {diagnosis.explanation}
      </div>
      {diagnosis.suggestion && (
        <div className="dagent-diagnosis__suggestion">
          <Lightbulb size={12} aria-hidden="true" /> {diagnosis.suggestion}
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ step, actions, canDecide, onDecide, onUndoAction, onUndoBatch }) {
  const card = step.approval;
  const liveById = useMemo(() => new Map((actions || []).map((action) => [action.id, action])), [actions]);
  const rows = card.actions.map((action) => ({ ...action, live: liveById.get(action.actionId) }));
  const pending = rows.filter((row) => !row.live || row.live.status === 'PENDING_APPROVAL');
  const awaiting = canDecide && pending.length > 0;
  const [selected, setSelected] = useState(() => new Set(card.actions.map((action) => action.actionId)));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!awaiting) setBusy(false);
  }, [awaiting]);

  const external = rows.some((row) => row.risk === 'EXTERNAL');
  const allSelected = selected.size === rows.length;
  const undoable = rows.filter((row) => row.live?.canUndo);
  const manualMinutes = rows.reduce((total, row) => total + (row.manualMinutes || 0), 0);

  const toggle = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async (ids) => {
    setBusy(true);
    try {
      await onDecide(step.runId, ids);
    } catch (error) {
      setBusy(false);
    }
  };

  return (
    <section className={`dagent-approval ${awaiting ? 'is-awaiting' : 'is-decided'}`} aria-label="Approval request">
      <header className="dagent-approval__head">
        <span className="dagent-approval__icon">
          <ShieldCheck size={16} aria-hidden="true" />
        </span>
        <div className="dagent-approval__title">
          <div className="dagent-kicker">{awaiting ? 'Needs your approval' : 'Proposed actions'}</div>
          <h4>
            {toolLabel(step.name)} · {rows.length} {rows.length === 1 ? 'item' : 'items'}
          </h4>
        </div>
        {manualMinutes > 0 && <span className="dagent-approval__estimate">about {Math.round(manualMinutes)} min by hand</span>}
      </header>

      {external && (
        <div className="dagent-banner dagent-banner--gold">
          <Mail size={14} aria-hidden="true" /> These messages leave the PMS and cannot be recalled once sent.
        </div>
      )}

      <ul className="dagent-approval__list">
        {rows.map((row) => {
          const status = row.live?.status || 'PENDING_APPROVAL';
          return (
            <li key={row.actionId} className={`dagent-approval__row is-${status.toLowerCase()}`}>
              <div className="dagent-approval__select">
                {awaiting ? (
                  <input
                    type="checkbox"
                    checked={selected.has(row.actionId)}
                    onChange={() => toggle(row.actionId)}
                    disabled={busy}
                    aria-label={`Select ${row.summary}`}
                  />
                ) : (
                  <StatusIcon status={status} />
                )}
              </div>
              <div className="dagent-approval__main">
                <div className="dagent-approval__summary">{row.summary}</div>
                <Preview preview={row.preview} />
                {(row.warnings || []).length > 0 && (
                  <div className="dagent-chips">
                    {row.warnings.map((warning) => (
                      <span key={warning} className="dagent-chip dagent-chip--warn">
                        <AlertTriangle size={11} aria-hidden="true" />
                        {warning}
                      </span>
                    ))}
                  </div>
                )}
                {row.live?.error && status !== 'REJECTED' && <div className="dagent-approval__error">{row.live.error}</div>}
                {status === 'FAILED' && <Diagnosis diagnosis={row.live?.result?.diagnosis} />}
                {status === 'SUCCEEDED' && row.live?.undoResult?.error && (
                  <div className="dagent-approval__error">Undo refused: {row.live.undoResult.error}</div>
                )}
              </div>
              <div className="dagent-approval__side">
                {!awaiting && <span className={`dagent-badge dagent-badge--${status.toLowerCase()}`}>{ACTION_STATUS_LABEL[status] || status}</span>}
                {!awaiting && row.risk === 'EXTERNAL' && status === 'SUCCEEDED' && <span className="dagent-badge dagent-badge--muted">Sent</span>}
                {row.live?.canUndo && onUndoAction && (
                  <button type="button" className="dagent-btn dagent-btn--ghost dagent-btn--sm" onClick={() => onUndoAction(row.actionId)}>
                    <Undo2 size={13} aria-hidden="true" /> Undo
                  </button>
                )}
                {row.live?.canUndo && row.live?.undoResult?.conflict && onUndoAction && (
                  <button type="button" className="dagent-btn dagent-btn--danger dagent-btn--sm" onClick={() => onUndoAction(row.actionId, true)}>
                    Force undo
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {card.skipped?.length > 0 && (
        <details className="dagent-approval__skipped">
          <summary>{card.skipped.length} not proposed</summary>
          <ul>
            {card.skipped.map((skipped, index) => (
              <li key={index}>
                <strong>{skipped.summary}</strong> — {skipped.reason}
                {skipped.suggestion && (
                  <div className="dagent-approval__suggestion">
                    <Lightbulb size={11} aria-hidden="true" /> {skipped.suggestion}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {(awaiting || (undoable.length > 1 && onUndoBatch)) && (
        <footer className="dagent-approval__foot">
          {awaiting ? (
            <>
              <label className="dagent-check-all">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.actionId)))}
                  disabled={busy}
                />
                Select all
              </label>
              <span className="dagent-spacer" />
              <button type="button" className="dagent-btn dagent-btn--ghost" disabled={busy} onClick={() => submit([])}>
                <X size={14} aria-hidden="true" /> Reject
              </button>
              <button type="button" className="dagent-btn dagent-btn--primary" disabled={busy || selected.size === 0} onClick={() => submit([...selected])}>
                {busy ? <Loader2 size={14} className="dagent-spin" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
                {allSelected ? `Approve all ${rows.length}` : `Approve ${selected.size} of ${rows.length}`}
              </button>
            </>
          ) : (
            <>
              <span className="dagent-spacer" />
              <button type="button" className="dagent-btn dagent-btn--ghost" onClick={() => onUndoBatch(undoable.map((row) => row.actionId))}>
                <Undo2 size={14} aria-hidden="true" /> Undo these {undoable.length}
              </button>
            </>
          )}
        </footer>
      )}
    </section>
  );
}

export default ApprovalCard;
